import { Grid, Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  FormControlLabel,
  Icons,
  Link,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { AimsFile } from '../../models/models';
import { SPECIAL_CHARACTER_ERROR } from '../../utilities/constants';
import { useNotification } from '../../utilities/useNotification';
import { useWindowDimensions } from '../../utilities/useWindowDimensions';
import {
  checkValidExtension,
  getFileExtension,
  hasSpecialCharacter,
} from '../../utilities/utilityFunctions';
import Thumbnail from '../FileSelectionViewThumbnail';
import useStyles from './FileSelectionView.styles';

interface Props {
  selectedFilesProps: AimsFile[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<AimsFile[]>>;
  setFilesToUpload: React.Dispatch<React.SetStateAction<AimsFile[]>>;
  filesToUpload: AimsFile[];
  onOpenFileUpload: () => void;
  openFileUploadView: boolean;
  handleFileUploadViewClose: () => void;
  checkShowOnReportCount: () => boolean;
  isDirectFileUpload: boolean;
  handleDirectFileUpload: (files: AimsFile[]) => void;
  isLoading: boolean;
  isEditable: boolean;
  setIsChange?: (val: boolean) => void;
}

const FileSelectionView: React.FC<Props> = ({
  selectedFilesProps,
  setSelectedFiles,
  setFilesToUpload,
  filesToUpload,
  onOpenFileUpload,
  openFileUploadView,
  handleFileUploadViewClose,
  checkShowOnReportCount,
  isDirectFileUpload,
  handleDirectFileUpload,
  isLoading = false,
  isEditable,
  setIsChange,
}: Props) => {
  const classes = useStyles();
  const size = useWindowDimensions();
  const { errorNotification } = useNotification();
  const selectedFiles = selectedFilesProps;
  const [isShowOnReport, setIsShowOnReport] = useState<boolean>(false);
  const [indexOfInvalidNames, setIndexOfInvalidNames] = useState<number[]>([]);
  const [openStorageLimitMsg, setOpenStorageLimitMsg] = useState<boolean>(false);
  const [storageDetails, setStorageDetails] = useState({
    used: '',
    totalSize: '',
    availableStorage: '',
  });
  const handleOnSave = () => {
    let filesToPush: AimsFile[] = [];
    selectedFiles.map(async (file, index) => {
      let ext = getFileExtension(file.updatedName);
      const isValidExtension = checkValidExtension(file.updatedName);
      if (ext === undefined || !isValidExtension) {
        ext = getFileExtension(file.name);
        selectedFiles[index].updatedName = file.updatedName + '.' + ext;
      }
      filesToPush = [...filesToPush, file];
    });
    setIsChange?.(true);
    setFilesToUpload([...filesToUpload, ...filesToPush]);
    if (isDirectFileUpload) {
      handleDirectFileUpload(filesToPush);
    } else {
      handleFileUploadViewClose();
    }
  };

  const onFileDelete = (fileToDelete: AimsFile) => {
    setSelectedFiles(
      selectedFiles.filter((image: AimsFile) => image.updatedName !== fileToDelete.updatedName),
    );
  };

  const updateFileList = (updatedFileList: AimsFile[]) => {
    setSelectedFiles(updatedFileList);
  };

  const validateName = (fileNameLength: number, index: number) => {
    const isValidFileName = hasSpecialCharacter(selectedFiles[index].updatedName);
    if (fileNameLength === 0 || isValidFileName) {
      if (!indexOfInvalidNames.includes(index))
        setIndexOfInvalidNames([...indexOfInvalidNames, index]);
      if (isValidFileName) {
        errorNotification(SPECIAL_CHARACTER_ERROR);
        return;
      }
    } else {
      const fileIndex = indexOfInvalidNames.findIndex(value => value === index);
      if (fileIndex !== -1) {
        indexOfInvalidNames.splice(fileIndex, 1);
      }
      setIndexOfInvalidNames(indexOfInvalidNames.slice());
    }
  };

  const handleOnSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (
      selectedFiles.filter(
        val => !val.updatedName || val.updatedName.trim().split('.')[0].length === 0,
      ).length === 0
    ) {
      handleOnSave();
    }
  };

  useEffect(() => {
    if (checkShowOnReportCount()) {
      setIsShowOnReport(true);
    } else {
      setIsShowOnReport(false);
    }
  }, [checkShowOnReportCount]);

  const checkStorage = () => {
    navigator.storage.estimate().then(estimate => {
      if (estimate?.usage && estimate?.quota) {
        const selectedFilesSize = selectedFiles?.reduce((sum, item) => sum + item.size, 0);
        const browserStorage = ((estimate?.usage + selectedFilesSize) / estimate?.quota) * 100;
        const availableValue = (0.99 * estimate?.quota - estimate?.usage) / 1000000 / 1024;
        setStorageDetails({
          used: ((estimate?.usage + selectedFilesSize) / 1000000 / 1024).toFixed(2),
          totalSize: (estimate?.quota / 1000000 / 1024).toFixed(2),
          availableStorage: (availableValue < 0 ? 0 : availableValue).toFixed(2),
        });
        if (browserStorage > 99) {
          setOpenStorageLimitMsg(true);
        } else {
          setOpenStorageLimitMsg(false);
        }
      }
    });
  };

  useEffect(() => {
    checkStorage();
  }, [selectedFiles, openStorageLimitMsg]);

  return (
    <>
      <Dialog
        onClose={handleFileUploadViewClose}
        fullScreen
        open={openFileUploadView}
        titleText="Add Files"
        backButton={false}
        dialogActions={
          <Grid
            container
            justifyContent="flex-end"
            alignItems="center"
            className={classes.deficiencyAddFilesDialogCont}
          >
            <Grid item className={classes.deficiencyAddFilesDialogGrid}>
              <Link
                to=""
                onClick={() => !isLoading && handleFileUploadViewClose()}
                color="primary"
                className={classes.deficiencyAddFilesCancelLink}
              >
                Cancel
              </Link>
            </Grid>
            <Grid item>
              <Button
                noMinHeight
                color="primary"
                variant="contained"
                className={classes.addPhotosSaveBtn}
                onClick={handleOnSave}
                disabled={
                  selectedFiles.length === 0 ||
                  indexOfInvalidNames.length > 0 ||
                  isLoading ||
                  openStorageLimitMsg
                }
                isLoading={isLoading}
                data-testid="save-inspection-files"
              >
                Save
              </Button>
            </Grid>
          </Grid>
        }
        maxWidth="lg"
      >
        <form onSubmit={handleOnSubmit}>
          <Card elevation={1} className={classes.card}>
            <Grid container spacing={2}>
              <Grid item container xs={12} alignItems="center">
                <Grid item xs={6}>
                  <Typography variant="h3" fontWeight={300}>
                    {`Selected Files (${selectedFiles?.length})`}
                  </Typography>
                </Grid>

                {isEditable && (
                  <Grid item xs={6} container justifyContent="flex-end" alignItems="center">
                    <Grid item>
                      <Button
                        noMinHeight
                        color="primary"
                        variant="contained"
                        className={classes.AddButton}
                        onClick={onOpenFileUpload}
                        data-testid="file-selection-view-add-more"
                      >
                        <Icons.PlusFeather /> Add...
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid container>
                {selectedFiles.map((data, index) => {
                  return (
                    <Grid
                      item
                      xs={size.height > size.width ? 12 : 6}
                      key={`editDeficiency-selected-${data.name}-${data.lastModified}`}
                    >
                      <Card elevation={1} className={classes.card}>
                        <Grid xs={12} className={classes.deficiencyUpdateImageGrid}>
                          <Grid item xs={6}>
                            <TextField
                              onChange={e => {
                                selectedFiles[index].updatedName = e.target.value;
                                validateName(e.target.value.length, index);
                              }}
                              error={indexOfInvalidNames.includes(index)}
                              errorText="Required or Invalid Name"
                              noMinHeight
                              variant="outlined"
                              size="small"
                              label="File name"
                              defaultValue={data.updatedName ? data.updatedName : data.name}
                              className="deficiencyUpdateImageFileName"
                              inputProps={{
                                'data-testid': `file-name-${selectedFiles[index].updatedName}`,
                              }}
                            />
                            <TextField
                              onChange={e => {
                                selectedFiles[index].fileDescription = e.target.value;
                              }}
                              label="File description"
                              noMinHeight
                              variant="outlined"
                              size="small"
                              placeholder="Type File Description"
                              multiline
                              rows={2}
                              rowsMax={4}
                              defaultValue={data.fileDescription ? data.fileDescription : ''}
                              className="deficiencyUpdateImageFileDesc"
                              inputProps={{
                                'data-testid': `file-description-${selectedFiles[index].updatedName}`,
                              }}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  disabled={
                                    isShowOnReport &&
                                    !data.showOnReport &&
                                    (data.type === 'image/jpeg' ||
                                      data.type === 'image/png' ||
                                      data.type === 'image/jpg')
                                  }
                                  defaultChecked={data.showOnReport ? data.showOnReport : false}
                                  onChange={e => {
                                    selectedFiles[index].showOnReport = e.target.checked;
                                    setIsShowOnReport(checkShowOnReportCount());
                                  }}
                                  data-testid={`file-showOnReport-${selectedFiles[index].updatedName}`}
                                />
                              }
                              label="Show on report"
                            />
                          </Grid>
                          <Grid xs={6}>
                            <Thumbnail
                              fileData={data}
                              index={index}
                              fileList={selectedFiles}
                              uniqueId={`${uuidv4()}`}
                              onDelete={onFileDelete}
                              updateFileList={updateFileList}
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Card>
        </form>
      </Dialog>
      {openStorageLimitMsg && (
        <Snackbar open={openStorageLimitMsg}>
          <Alert
            icon={false}
            variant="filled"
            color="error"
            className={classes.muiAlert}
            onClose={() => setOpenStorageLimitMsg(false)}
          >
            Less Memory Available to Save the Files. Please use files with smaller size. Memory
            available is {storageDetails.availableStorage} GB
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default FileSelectionView;
